import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { staffApi, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { calendarViewScope } from "@/lib/permissions";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  CalendarClock, AlertTriangle, MapPin, ChevronRight, LogOut, Users,
} from "lucide-react";
import Logo from "@/components/layout/Logo";
import CareTeamTabs from "@/components/layout/CareTeamTabs";
import {
  dayNumber,
  monthShort,
  LEAVE_LABEL,
  resolveRosterDayTarget,
  deriveRosterDayStatus,
  rosterDayTappable,
} from "@/lib/RosterEngine";

/**
 * Mobile-first Care Team Dispatch — 10-day roster for practitioners with
 * only `personal_view`. Each day is a tap target that jumps into the
 * per-branch Daily Worklist. Multi-branch days are highlighted in red so
 * the operator sees potential double-booking immediately.
 */
export default function MyRoster() {
  const { staff, logoutStaff } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [roster, setRoster] = useState(null);
  const [lockInfo, setLockInfo] = useState(null);

  const scope = useMemo(() => calendarViewScope(staff), [staff]);

  useEffect(() => {
    let mounted = true;
    const fetchRoster = async () => {
      try {
        const [r, l] = await Promise.all([
          staffApi.get("/staff/my-roster", { params: { days: 10 } }),
          staffApi.get("/staff/my-medical-lock").catch(() => ({ data: { locked: false } })),
        ]);
        if (mounted) { setRoster(r.data); setLockInfo(l.data); }
      } catch (err) {
        if (mounted) toast.error(formatApiError(err));
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchRoster();
    // Bug fix — refetch the compliance surface every time the tab
    // regains focus (specialist tapped out to fill a record and came
    // back) so a completed date immediately drops off the roster.
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchRoster();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    // Custom event fired by MedicalRecordDialog on save so a mounted
    // MyRoster (rare — remount usually covers it) re-scans instantly.
    const onSaved = () => fetchRoster();
    window.addEventListener("medical-record-saved", onSaved);
    return () => {
      mounted = false;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      window.removeEventListener("medical-record-saved", onSaved);
    };
  }, []);

  const tapDay = (day) => {
    // Roster now shows every day — tapping opens the worklist even
    // when there are no bookings yet, so the practitioner can still
    // review their schedule detail (Duty branch, hours) at that
    // location. Multi-branch conflict days pass all duty branches so
    // the worklist page can flip tabs. Past red compliance days route
    // through the same worklist so the specialist can fill the
    // outstanding medical reports exactly like today's clients.
    // Delegated to RosterEngine.resolveRosterDayTarget so the routing
    // rule is single-sourced across surfaces.
    const target = resolveRosterDayTarget(day);
    if (!target) {
      toast.info("Day Off — nothing scheduled at any branch.");
      return;
    }
    nav(`/staff/my-roster/day?date=${target.date}&branch_id=${target.branch_id}&all_branches=${encodeURIComponent(target.all_branches)}`);
  };

  return (
    <div className="min-h-screen bg-[hsl(40_20%_97%)] pb-20 sm:pb-6">
      <header className="sticky top-0 z-10 h-14 px-4 sm:px-6 border-b border-border bg-white/95 backdrop-blur flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo variant="mark" to={null} size={32} testid="myroster-brand-logo" />
          <div className="hidden sm:block">
            <p className="text-[11px] tracking-overline text-muted-foreground">CARE TEAM DISPATCH</p>
            <p className="text-sm font-semibold leading-tight">My 10-Day Roster</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground max-w-[8rem] truncate">{roster?.staff_name || staff?.full_name}</span>
          {scope.canToggle ? (
            <Button variant="ghost" size="sm" onClick={() => nav("/staff/branches")} data-testid="myroster-switch-workspace">
              <Users className="w-4 h-4 mr-1" /> All views
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" onClick={() => { logoutStaff(); nav("/staff/login"); }} data-testid="myroster-logout-btn">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <div className="hidden sm:block max-w-2xl mx-auto pt-4 px-3 sm:px-6">
        <CareTeamTabs />
      </div>

      <div className="max-w-2xl mx-auto px-3 sm:px-6 py-6">
        <div className="mb-5 sm:hidden">
          <p className="text-[11px] tracking-overline text-muted-foreground">CARE TEAM DISPATCH</p>
          <h1 className="text-2xl font-semibold mt-1">My 10-Day Roster</h1>
          <p className="text-xs text-muted-foreground mt-1">Tap any day to open your client worklist.</p>
        </div>
        <div className="hidden sm:block mb-6">
          <h1 className="text-2xl font-semibold" data-testid="myroster-heading">Where you&apos;re needed next</h1>
          <p className="text-sm text-muted-foreground mt-1">Tap a day to load its client worklist at the correct branch.</p>
        </div>

        {lockInfo?.locked && (
          <div
            className="mb-5 rounded-xl border-2 border-red-400 bg-red-50 p-4 flex items-start gap-3"
            role="alert"
            data-testid="myroster-lock-banner"
          >
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-red-800">
                Account Locked
              </p>
              <p className="text-xs text-red-800 mt-0.5">
                Please complete your missing medical reports from{" "}
                <b>{lockInfo.oldest_incomplete_date || "yesterday"}</b> to proceed.
              </p>
              <p className="text-[11px] text-red-700 mt-1">
                {lockInfo.incomplete_count} incomplete report{lockInfo.incomplete_count === 1 ? "" : "s"} · AI Summary + Patient History are disabled for today&apos;s and upcoming clients until every past record is saved.
              </p>
            </div>
          </div>
        )}

        {loading && (
          <div className="space-y-3" data-testid="myroster-loading">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse h-24 rounded-xl bg-white border border-border" />
            ))}
          </div>
        )}

        {!loading && roster && roster.days.length === 0 && (
          <div className="text-center py-14 text-sm text-muted-foreground">Nothing scheduled in the next 10 days.</div>
        )}

        {!loading && roster && (
          <div className="space-y-3" data-testid="myroster-list">
            {roster.days.map((day) => {
              const isMissing = !!day.missing_report;
              const leaveType = day.leave_type || null;  // public_holiday | annual_leave | sick_leave
              // Delegated to RosterEngine so the status derivation is
              // single-sourced across surfaces.
              const status = deriveRosterDayStatus(day);
              const isConflict = status === "conflict";
              const isDayOff = status === "day_off" || !!leaveType;
              const leaveBadge = leaveType ? LEAVE_LABEL[leaveType] : null;
              const dutyBranches = day.duty_branches || [];
              const hasBookings = day.total_appointments > 0;
              // Past red compliance days are always tappable so the
              // specialist can jump straight into the outstanding
              // reports. Yesterday is ALWAYS tappable — even on a
              // day-off with zero bookings — so late-added notes /
              // photos can still be filed within the edit window.
              const tappable = rosterDayTappable(day);
              return (
                <button
                  key={day.date}
                  type="button"
                  onClick={() => tapDay(day)}
                  disabled={!tappable}
                  data-testid={`myroster-day-${day.date}`}
                  data-status={status}
                  data-conflict={isConflict ? "1" : "0"}
                  data-missing-report={isMissing ? "1" : "0"}
                  className={[
                    "w-full text-left rounded-xl transition-all duration-200",
                    "border shadow-sm active:scale-[0.995]",
                    isMissing
                      ? "bg-red-50 border-red-400 hover:border-red-500 hover:shadow-md ring-1 ring-red-200"
                      : isConflict
                        ? "bg-red-50 border-red-300 hover:border-red-400 hover:shadow-md"
                        : isDayOff
                          ? "bg-white/70 border-border opacity-80 cursor-default"
                          : "bg-white border-border hover:border-primary hover:shadow-md",
                  ].join(" ")}
                >
                  <div className="p-4 flex items-stretch gap-4">
                    {/* Date pill */}
                    <div className={[
                      "flex flex-col items-center justify-center w-16 rounded-lg py-2",
                      isMissing
                        ? "bg-red-600 text-white"
                        : isConflict
                          ? "bg-red-100 text-red-800"
                          : (day.is_today ? "bg-primary text-primary-foreground" : "bg-secondary"),
                    ].join(" ")}>
                      <span className="text-[10px] uppercase tracking-wider">{day.weekday}</span>
                      <span className="text-2xl font-semibold leading-none mt-1">{dayNumber(day.date)}</span>
                      <span className="text-[10px] uppercase tracking-wider mt-1">{monthShort(day.date)}</span>
                    </div>

                    {/* Body */}
                    <div className="flex-1 min-w-0">
                      {isMissing && (
                        <div
                          className="flex items-center gap-1.5 text-[11px] font-semibold text-red-700 uppercase tracking-wider mb-1"
                          data-testid={`myroster-missing-${day.date}`}
                        >
                          <AlertTriangle className="w-3.5 h-3.5" />
                          Missing medical report
                        </div>
                      )}
                      {day.is_yesterday && !isMissing && (
                        <div
                          className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-800 uppercase tracking-wider mb-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5"
                          data-testid={`myroster-yesterday-badge-${day.date}`}
                        >
                          Yesterday · edit window
                        </div>
                      )}
                      {isConflict && !isMissing && (
                        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-red-700 uppercase tracking-wider mb-1" data-testid={`myroster-conflict-${day.date}`}>
                          <AlertTriangle className="w-3.5 h-3.5" />
                          {day.duty_message || day.conflict_message}
                        </div>
                      )}
                      {isDayOff && !isMissing && (
                        <div className="flex items-center gap-2" data-testid={`myroster-dayoff-${day.date}`}>
                          {leaveBadge && (
                            <span
                              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${leaveBadge.cls}`}
                              data-testid={`myroster-leave-badge-${day.date}`}
                              data-leave-type={leaveType}
                              title={leaveBadge.full}
                            >
                              {leaveBadge.text}
                            </span>
                          )}
                          <div className="text-sm text-muted-foreground italic">
                            {leaveBadge ? `Status: ${leaveBadge.full}` : "Status: Day Off"}
                          </div>
                        </div>
                      )}
                      {isMissing && (day.branches || []).length > 0 && (
                        (day.branches || []).map((b) => (
                          <div key={`m-${b.branch_id}`} className="flex items-center gap-2 py-0.5" data-testid={`myroster-missing-branch-${day.date}-${b.branch_id}`}>
                            <MapPin className="w-3.5 h-3.5 shrink-0 text-red-600" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">
                                {b.branch_name}
                              </div>
                              <div className="text-xs text-muted-foreground flex items-center gap-1">
                                <CalendarClock className="w-3 h-3" />
                                {b.first_start} – {b.last_end}
                                <span className="ml-1">· {b.appointment_count} client{b.appointment_count === 1 ? "" : "s"} pending</span>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                      {!isDayOff && !isMissing && dutyBranches.map((b) => (
                        <div key={b.branch_id} className="flex items-center gap-2 py-0.5" data-testid={`myroster-duty-${day.date}-${b.branch_id}`}>
                          <MapPin className={`w-3.5 h-3.5 shrink-0 ${isConflict ? "text-red-600" : "text-primary"}`} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">
                              Duty: {b.branch_name}
                            </div>
                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                              <CalendarClock className="w-3 h-3" />
                              {b.work_start && b.work_end ? `${b.work_start} – ${b.work_end}` : "Hours TBD"}
                              {(() => {
                                const brBook = (day.branches || []).find((x) => x.branch_id === b.branch_id);
                                if (!brBook) return null;
                                return (
                                  <span className="ml-1">· {brBook.appointment_count} client{brBook.appointment_count === 1 ? "" : "s"}</span>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      ))}
                      {/* Bookings-only branches (e.g. an assist tile at a
                          branch the person isn't officially rostered at) —
                          rare, but surfaced so nothing hides. */}
                      {!isDayOff && !isMissing && day.branches.filter((b) => !dutyBranches.some((d) => d.branch_id === b.branch_id)).map((b) => (
                        <div key={`b-${b.branch_id}`} className="flex items-center gap-2 py-0.5">
                          <MapPin className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm truncate">Bookings at <b>{b.branch_name}</b></div>
                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                              <CalendarClock className="w-3 h-3" />
                              {b.first_start} – {b.last_end}
                              <span className="ml-1">· {b.appointment_count} client{b.appointment_count === 1 ? "" : "s"}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {tappable && (
                      <ChevronRight className={`self-center w-5 h-5 ${isMissing || isConflict ? "text-red-600" : "text-muted-foreground"}`} />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="sm:hidden">
        <CareTeamTabs />
      </div>
    </div>
  );
}
